import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchAddonTemplate, fetchAddonTemplates, updateAddonTemplate, createAddonTemplate } from "../../actions/mine_craft/addonTemplatesActions";

export interface AddonTemplateInterface {
  url: string;

  id: number;
  title: string;
  body: string;
  search_text: string;
  thumbnail_url: string;
  mcpack_url: string;
  mcpack_second_url: string;
  category_id: number | undefined;
  subcategory_id: number | undefined;
  likes_count: number;
  comments_count: number;
  popular_count: number;
  downloads_count: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: AddonTemplateInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<AddonTemplateInterface>();

const slice = createSlice({
  name: 'addonTemplates',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAddonTemplates.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAddonTemplates.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.addon_templates;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchAddonTemplates.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchAddonTemplate.fulfilled, (state, action) => {
        const addonTemplate = Object.values(action.payload.addon_template)[0];
        state.entities[addonTemplate.id] = addonTemplate;
      })

      // #create
      .addCase(createAddonTemplate.fulfilled, (state, action) => {
        const addonTemplate = Object.values(action.payload.addon_template)[0];
        state.entities[addonTemplate.id] = addonTemplate;
      })
      .addCase(createAddonTemplate.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateAddonTemplate.fulfilled, (state, action) => {
        const addonTemplate = Object.values(action.payload.addon_template)[0];
        state.entities[addonTemplate.id] = addonTemplate;
      })
      .addCase(updateAddonTemplate.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectAddonTemplateById,
  selectIds: selectAddonTemplateIds,
  selectEntities: selectAddonTemplateEntities,
  selectAll: selectAllAddonTemplates,
  selectTotal: selectTotalAddonTemplates
} = adaptor.getSelectors<RootState>(state => state.mineCraft.addonTemplates);
